import {
  useGetOrganizationInvoiceSettingsQuery,
  usePutOrganizationInvoiceSettingsMutation,
} from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import { Box, Table, useOpenClose } from '@palmetto/palmetto-components';
import { useParams } from 'react-router-dom';

import { OrganizationInvoiceSettingV2DisplayMap, OrganizationInvoiceSettingsV2Key, ProgramType } from 'types';

import { InvoiceSettingsCard } from './InvoiceSettingsCard';
import { InvoiceSettingsEditModal } from './InvoiceSettingsEditModal';

interface InvoiceSettingsProps {
  organization: OrganizationNode;
  isLoading?: boolean;
  programType: ProgramType;
}
export const InvoiceSetting = ({ organization, programType }: InvoiceSettingsProps) => {
  const { alias } = useParams<{ alias: any }>();
  const {
    data,
    refetch: refetchInvoiceSettings,
    isLoading: isLoadingInvoiceSettings,
  } = useGetOrganizationInvoiceSettingsQuery({
    alias,
    programType,
  });

  const [setInvoiceSettings] = usePutOrganizationInvoiceSettingsMutation();

  const {
    handleClose: handleCloseInvoiceSettingModal,
    handleOpen: handleOpenInvoiceSettingModal,
    isOpen: isOpenInvoiceSettingsModal,
  } = useOpenClose();

  const rows = data
    ? Object.entries({
        invoiceDueDays: data.value?.invoiceDueDays ?? 0,
        installApprovalPauseDays: data.value?.installApprovalPauseDays ?? 0,
        materialsEstimatePauseDays: data.value?.materialsEstimatePauseDays ?? 0,
      }).map(([key, value]) => ({
        id: key,
        name: OrganizationInvoiceSettingV2DisplayMap[key as OrganizationInvoiceSettingsV2Key] || key,
        value,
      }))
    : [];

  const columnConfig = [
    {
      heading: 'Name',
      dataKey: 'name',
    },
    {
      heading: 'Value',
      dataKey: 'value',
    },
  ];

  const handleSubmit = async (values: any) => {
    const payload = {
      alias,
      invoiceSettings: {
        value: {
          invoiceDueDays: values.invoiceDueDays,
          installApprovalPauseDays: values.installApprovalPauseDays,
          materialsEstimatePauseDays: values.materialsEstimatePauseDays,
        },
        programType: programType,
        inherit: false,
      },
    };
    await setInvoiceSettings(payload).unwrap();
    handleCloseInvoiceSettingModal();
  };

  return (
    <>
      <InvoiceSettingsEditModal
        isOpen={isOpenInvoiceSettingsModal}
        handleClose={handleCloseInvoiceSettingModal}
        handleSubmit={handleSubmit}
        currentInvoiceSettings={data?.value}
        modalId="invoice-settings-modal"
        modalTitle="Edit Invoice Settings"
        submitButtonText="Save Changes"
        refetchInvoiceSettings={refetchInvoiceSettings}
      />
      <Box>
        <InvoiceSettingsCard
          cardTitle="Invoice Settings"
          organization={organization}
          isLoading={isLoadingInvoiceSettings}
          onClick={handleOpenInvoiceSettingModal}
        >
          <Box padding="0 0 xl 0">
            <Table rowKey="id" columns={columnConfig} rows={rows} isLoading={isLoadingInvoiceSettings} />
          </Box>
        </InvoiceSettingsCard>
      </Box>
    </>
  );
};
